import { authFetch, ownerParam } from './client';
import { getToken } from './auth';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const listFiles = () => authFetch('/api/repository');
// Copy the shared guided-tutorial example geometry into the caller's own repo
// (returns the new file record). The file is provisioned once at a fixed S3 key;
// see backend POST /api/repository/tutorial/clone. Idempotent by fileName.
export const cloneTutorialFile = (fileName) =>
  authFetch('/api/repository/tutorial/clone', {
    method: 'POST',
    body: JSON.stringify({ fileName }),
  });
// Same shape for a shared V&V benchmark case's geometry, over the `vnv-cases/`
// prefix; also idempotent by fileName, so re-running a benchmark reuses the one
// copy. `fileName` must be one the backend's committed case-library manifest
// lists — it is the allowlist, unlike the tutorial route above which accepts any
// name — so pass the `assets.cadFileName` a benchmark's detail reported rather
// than composing a name here.
export const cloneVnvCaseFile = (fileName) =>
  authFetch('/api/repository/vnv-case/clone', {
    method: 'POST',
    body: JSON.stringify({ fileName }),
  });
// `owner` + `projectId` are the SHARED read: a project share does not grant the
// repository file (that record has its own `file` ACL), so the backend derives
// access from the project's graph referencing this fileId. Both are needed —
// owner alone falls back to the direct file grant and 403s without one.
export const getDownloadUrl = (fileId, owner, projectId) =>
  authFetch(
    `/api/repository/${fileId}/download-url${ownerParam(owner)}` +
      (owner && projectId ? `&project_id=${encodeURIComponent(projectId)}` : '')
  );
// PARTIAL patch over `{fileName?, folderPath?, tags?}`. An OMITTED field is left
// alone; `tags: []` is the explicit "remove them all". So never build the body by
// spreading a whole file record — that would resend every field and turn a rename
// into a write of stale values.
export const updateFile = (fileId, patch) =>
  authFetch(`/api/repository/${fileId}`, { method: 'PATCH', body: JSON.stringify(patch) });
// Kept as its own name because CADInputPanel and the dashboard rail call it; it
// is now the one-field case of updateFile.
export const renameFile = (fileId, fileName) => updateFile(fileId, { fileName });
export const deleteFile = (fileId) => authFetch(`/api/repository/${fileId}`, { method: 'DELETE' });

// ── Organization ────────────────────────────────────────────────────────────
// A folder is a RECORD, not a value derived from the paths in use, so an empty
// folder survives a reload. Paths are materialized (`Geometry/Coupons`; '' is the
// root) and the BACKEND is the authority on what it will store — see
// `app/services/repo_paths.py`.
export const listFolders = () => authFetch('/api/repository/folders');
export const createFolder = (path) =>
  authFetch('/api/repository/folders', { method: 'POST', body: JSON.stringify({ path }) });
// Rename AND move are one call: both change the path, and either way the backend
// carries the descendants and their files along.
export const updateFolder = (folderId, path) =>
  authFetch(`/api/repository/folders/${folderId}`, {
    method: 'PATCH',
    body: JSON.stringify({ path }),
  });
// Reparents the contents to the folder above; never deletes a file.
export const deleteFolder = (folderId) =>
  authFetch(`/api/repository/folders/${folderId}`, { method: 'DELETE' });
// Bulk on purpose — a user drags several files onto a folder, and N round trips
// would leave a partial move visible if one failed. Moves metadata only: the S3
// key is untouched, so nothing downstream re-resolves.
export const moveFiles = (fileIds, folderPath) =>
  authFetch('/api/repository/files/move', {
    method: 'POST',
    body: JSON.stringify({ fileIds, folderPath }),
  });
// COPY is a real duplication — a new file_{uuid} row and a new S3 object each,
// so it costs storage where a move costs nothing. The backend suffixes a name
// that would collide in the destination (`gear (copy).stp`) rather than leaving
// two identical names, which the agent's name matcher refuses as ambiguous.
// Returns `{copied, folderPath, files:[{fileId, fileName}]}`.
export const copyFiles = (fileIds, folderPath) =>
  authFetch('/api/repository/files/copy', {
    method: 'POST',
    body: JSON.stringify({ fileIds, folderPath }),
  });

export async function uploadFile(file, onProgress) {
  const token = await getToken();
  if (!token) throw new Error('Not authenticated');

  // Get presigned upload URL
  const res = await fetch(`${BASE_URL}/api/repository/upload-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type || 'application/octet-stream',
    }),
  });
  if (!res.ok) throw new Error('Failed to get upload URL');
  const { uploadUrl, fileId } = await res.json();

  // Upload directly to S3 using presigned URL
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () =>
      xhr.status < 400 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });

  return fileId;
}

// Stream a file through the backend relay (POST /api/repository/upload) instead
// of the presigned-PUT path. Used by CADInputPanel's "Upload from disk" button:
// returns the full file record ({ fileId, fileName, fileSize, s3Key }) so the
// panel can load the new file immediately without a separate list round-trip.
export async function uploadFileDirect(file, onProgress) {
  const token = await getToken();
  const form = new FormData();
  form.append('file', file);

  return await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE_URL}/api/repository/upload`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status < 400) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch {
          reject(new Error('Bad response'));
        }
      } else {
        reject(new Error(`Upload failed: ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(form);
  });
}
